import i18n from "@/i18n";
import type { SelectedLabelsStore } from "./types";

export const t = (key: string): string => i18n.global.t(key);

// Formatea un número con n decimales fijos. Añade '+' si conSigno y el valor es positivo.
export const sFormateaConSigno = (
  valor: number | string | null | undefined,
  conSigno: boolean,
  decimales: number,
): string => {
  if (valor == null || valor === "") return "";
  const num = Number(valor);
  if (isNaN(num)) return "";
  const formatted = num.toFixed(decimales);
  return conSigno && num >= 0 ? `+${formatted}` : formatted;
};

// Prioridad: 1) valor de la API, 2) valor del store, 3) "".
export const getValueWithFallback = (apiValue: any, storeValue: any): string => {
  if (apiValue !== null && apiValue !== undefined && apiValue !== "") return String(apiValue);
  if (storeValue !== null && storeValue !== undefined && storeValue !== "") return String(storeValue);
  return "";
};

export const getValueWithSign = (apiValue: any, signo: any, storeValue: any): string => {
  const val = getValueWithFallback(apiValue, storeValue);
  if (val === "") return "";
  const num = Math.abs(Number(val));
  if (isNaN(num)) return val;
  return signo === "-" ? `-${num}` : `${num}`;
};

export const truncar = (str: string, limite = 50): string => {
  if (!str) return "";
  return str.length > limite ? str.slice(0, limite) : str;
};

export function obtenerBiselado(bisel: string, montaje: string): string {
  const parts: string[] = [];
  if (bisel) parts.push(t("biselado_remoto"));
  if (montaje) parts.push(t("montaje"));
  return parts.join(" / ");
}

export function obtenerPrecalibrado(precalibrado: string, borde: string, centro: string): string {
  if (precalibrado && precalibrado !== "B") return t("precalibrado");
  if (borde || centro) return t("espesor_especial");
  return "";
}

export function obtenerTipoLente(codigo_lente: string): string {
  if (codigo_lente == "01") return t("stock");
  if (codigo_lente == "02") return t("laboratorio");
  return "";
}

export function obtenerTexto(letra: string): string {
  switch (letra) {
    case "S": return t("forma_basica");
    case "M":
    case "G": return t("base_de_datos_de_marcas");
    case "C": return t("base_datos_mis_biselados");
    case "B": return t("biselado_remoto");
    case "F": return "ShapeScan";
    default: return "";
  }
}

export function obtenerDescBisel(ncode: string): string {
  switch (ncode) {
    case "03": return t("plano");
    case "05": return t("ranurada_metal");
    case "06": return t("ranurada_nylon");
    case "10": return t("bisel_normal");
    case "12": return t("bisel_seguridad");
    case "13": return t("bisel_envolvente_solar");
    default: return "";
  }
}

// Factory: crea los getters de labels enlazados a un selectedLabelsStore concreto.
// Devuelve un objeto con todos los getters para inyectar en el contexto del PDF.
export function createLabelGetters(selectedLabelsStore: SelectedLabelsStore) {
  const getDescentramiento = (valor: number | string | null | undefined): string =>
    valor !== null && valor !== undefined ? String(valor) : "";

  const getPrismaLabel = (
    prisma: { prisma1?: string; prisma2?: string } | null | undefined,
  ): string => [prisma?.prisma1, prisma?.prisma2].filter(Boolean).join("  ");

  return {
    getFormaLenteLabel: (): string => selectedLabelsStore.formaLente || "",
    getFotocromaticoLabel: (): string => selectedLabelsStore.fotocromatico || "",
    getColoracionLabel: (): string => selectedLabelsStore.coloracion || "",
    getColorLabel: (): string => selectedLabelsStore.color || "",
    getTonoLabel: (): string => selectedLabelsStore.tono || "",
    getTratamientoLabel: (): string => selectedLabelsStore.tratamiento || "",
    getBaseEspecialODLabel: (): string => selectedLabelsStore.baseEspecialOD || "",
    getBaseEspecialOILabel: (): string => selectedLabelsStore.baseEspecialOI || "",
    getPrecalibradoLabel: (): string => selectedLabelsStore.precalibrado || "",
    getDescentramientoOD: (): string => getDescentramiento(selectedLabelsStore.descentramientoOD),
    getDescentramientoOI: (): string => getDescentramiento(selectedLabelsStore.descentramientoOI),
    getPrismaODLabel: (): string => getPrismaLabel(selectedLabelsStore.prismaOD),
    getPrismaOILabel: (): string => getPrismaLabel(selectedLabelsStore.prismaOI),
    getBiseladoMontajeLabel: (): string =>
      [selectedLabelsStore.biselado, selectedLabelsStore.montaje].filter(Boolean).join(" / "),
  };
}

export type LabelGetters = ReturnType<typeof createLabelGetters>;

// Busca CARACTERISTICA en focomateriales y devuelve su DESC_CARACT.
export const getFocoMaterialLabel = (
  caracteristica: string,
  focomateriales: any[] | undefined,
): string => {
  if (!caracteristica) return "";
  try {
    const item = (focomateriales || []).find((f: any) => f.CARACTERISTICA === caracteristica);
    return item?.DESC_CARACT || "";
  } catch {
    return "";
  }
};

// Etiqueta de ojo (OD / OI) con el sufijo " (I)" cuando el ojo es solo
// informativo (SOLO_INFOR === "X" en el pedido).
export const getEtiquetaOjo = (base: string, soloInfor: any): string =>
  soloInfor === "X" || soloInfor === true ? `${base} (I)` : base;

// Valor de "ojo informativo" a pintar, dando prioridad al pedido.
//
// A diferencia del resto de campos (esfera, cilindro…), aquí NO vale el
// fallback por valor vacío: SOLO_INFOR === "" es un valor legítimo ("no
// informativo") y caer al formulario en ese caso haría que un pedido
// recuperado de SAP (o cualquiera de una impresión en lote) heredase el check
// que quedara en el formulario en curso. Por eso el formulario solo se consulta
// cuando el pedido no trae NINGÚN dato de ese ojo, es decir un pedido nuevo que
// todavía no ha pasado por `preparePedido`.
//
// @param ojoBapi ojo devuelto por `getOjoIn` (nunca nulo; `{}` si no hay datos)
// @param formInformativo `pedidosStore.od/oi.informativo` (check del formulario)
export const resolveSoloInfor = (
  ojoBapi: Record<string, unknown>,
  formInformativo: boolean | undefined,
): string | boolean | undefined =>
  Object.keys(ojoBapi).length > 0
    ? (ojoBapi.SOLO_INFOR as string | undefined)
    : formInformativo;
